import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { BatchStatus } from "../../types/batch";

export interface BatchVideoItem {
  id: number;
  brand_id: number;
  ad_asset_group_id: number;
  source_id: number;
  source_type: string;
  is_rejected: boolean;
  is_accepted: boolean;
  batch_id: number;
  aspect_ratio: string;
  prompt: string;
  s3_path: string;
  model_name: string;
  model_info: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface VideoBatch {
  batchId: string;
  ad_asset_group_id: number | null;
  status: BatchStatus | "idle";
  count: number;
  prompt: string;
  error?: string;
  lastPolled?: string;
}

// Base atom for batch video state - now an array to track multiple batches
// Persist to localStorage so batches survive page refreshes
export const batchVideoAtom = atomWithStorage<VideoBatch[]>("video-batches", []);

// Derived atom for adding a new batch and starting polling
export const addBatchAtom = atom(
  null,
  (get, set, args: { batchId: string; adAssetGroupId?: string | number | null; prompt: string; count?: number }) => {
    const current = get(batchVideoAtom);
    // Check if batch already exists
    const existingBatch = current.find((batch) => batch.batchId === args.batchId);
    if (existingBatch) {
      return; // Batch already exists, don't add duplicate
    }

    const newBatch: VideoBatch = {
      batchId: args.batchId,
      ad_asset_group_id: args.adAssetGroupId ? Number(args.adAssetGroupId) : null,
      status: BatchStatus.Processing,
      count: args.count || 0,
      prompt: args.prompt || "Video generation"
    };

    set(batchVideoAtom, [...current, newBatch]);
  }
);

// Derived atom for removing a batch
export const removeBatchAtom = atom(null, (get, set, batchId: string) => {
  const current = get(batchVideoAtom);
  set(
    batchVideoAtom,
    current.filter((batch) => batch.batchId !== batchId)
  );
});

// Derived atom for getting batches for a specific ad asset group
export const batchesForAdAssetGroupAtom = atom((get) => {
  return (adAssetGroupId: number | string | null | undefined) => {
    const batches = get(batchVideoAtom);
    if (!adAssetGroupId) return [];
    return batches.filter((batch) => batch.ad_asset_group_id === Number(adAssetGroupId));
  };
});

// Derived atom for getting all active batches (non-idle)
export const activeBatchesAtom = atom((get) => {
  const batches = get(batchVideoAtom);
  return batches.filter((batch) => batch.status !== "idle");
});
